/**
 * Tour definitions — one guided mini-tour per Welcome Checklist step.
 * Popup copy is Tim's (Beta-1 review tickler rows A13, A17–A18, A21–A23, A28–A29 and D02).
 *
 * Step schema:
 *   route      — navigate here before showing the step
 *   target     — data-tour id to spotlight (null = centered card)
 *   title/body — popover copy. Body tells the user exactly what to DO,
 *                then they press Next. Never traps: Next always works.
 *   placement  — preferred popover side ('bottom' default)
 *
 * Targets live in the pages as data-tour attributes:
 *   scope-filter, reps-quadrants, risk-alerts, notifications-bell,
 *   survey-list, members-roles, add-member, import-org, view-as
 *
 * Finishing a tour marks its checklist step complete (OnboardingProvider),
 * and doing the real thing (filtering to a site, submitting a survey, adding a
 * member, approving an import) marks it too.
 */

const DA = '/drew';

export const TOURS = {
  'find-the-fire': [
    {
      route: DA,
      target: null,
      title: 'Find the Fire',
      body: "Company-wide, Magnum looks fine — yellow-ish, nothing alarming. That's the point: averages hide fires. Let's find one.",
    },
    {
      route: DA,
      target: 'scope-filter',
      title: 'Filter by site',
      body: 'Use the Site dropdown here and pick Houston Plant. Watch what happens to the numbers, then come back to Next.',
      placement: 'bottom',
    },
    {
      route: DA,
      target: 'reps-quadrants',
      title: 'The four REPS quadrants',
      body: 'Reliability, Efficiency, Productivity, Safety. Higher is worse: green below 4, yellow 4–6, red above 6. With Houston selected, all four are red — and the trend has been climbing since late May. Click any card to see which elements are driving it.',
      placement: 'bottom',
    },
    {
      route: DA,
      target: 'notifications-bell',
      title: 'The warnings fired weeks ago',
      body: 'Open the bell: threshold alerts fired the moment Houston crossed the line — not today, weeks ago. Early warning is the whole product.',
      placement: 'bottom',
    },
  ],

  'read-the-alert': [
    {
      route: DA,
      target: 'risk-alerts',
      title: 'The risk alert',
      body: 'This is the critical Houston alert. Expand it and read its three parts: what is accumulating (shift-handover drift), why it matters (this signature precedes real events), and what to do about it.',
      placement: 'bottom',
      missingHint:
        'No alerts on this dashboard — you may be viewing an organization without any. Switch the Organization picker to your Magnum sandbox and replay this step.',
    },
    {
      route: DA,
      target: null,
      title: 'Reverse / Eliminate / Fortify',
      body: 'Every alert ends in three concrete moves — the REF Formula from your training: Reverse Drift. Eliminate Setups. Fortify Defenses. Not generic advice — remedial direction. There is a second, major alert too: the same pattern rising at two facilities on two continents. Individually "monitor"; together, systemic.',
    },
  ],

  'move-the-numbers': [
    {
      route: `${DA}/surveys`,
      target: 'survey-list',
      title: 'Complete a survey',
      body: 'All data currently in the system is historical. Now is your opportunity to see how survey inputs impact DREW. Begin with a facility currently showing healthy metrics (such as Dublin) and complete the survey as if you and your team are having a terrible month (high numbers = high entropy). Once you\'ve completed your survey, you will see how the metrics shift. You can repeat the survey as many times as you like.',
      placement: 'top',
    },
    {
      route: `${DA}/surveys`,
      target: null,
      title: 'Watch the numbers & metrics shift',
      body: 'Once you\'ve completed your survey(s), you can see how the data, metrics, and alerts (as applicable) have shifted, in real time. No batch job, no overnight refresh — DREW reads signals as they land.',
    },
  ],

  'add-your-people': [
    {
      route: '/settings/organization',
      target: 'members-roles',
      title: 'Members & Roles',
      body: 'This is where REPSit.ai becomes part of the team. Each team member is given access at a level appropriate for their role. For example, a Site Manager sees their site; a Department Head sees their department; an Individual Contributor sees a snapshot of their Natural Work Team (NWT).',
      placement: 'top',
    },
    {
      route: '/settings/organization',
      target: 'add-member',
      title: 'Add team member(s)',
      body: 'Click Add Member and invite a colleague, or use a second email of your own. Assign role(s). Added users will receive a temporary password. The data they will see will be based on their assigned role.',
      placement: 'top',
    },
    {
      route: '/settings/organization',
      target: 'view-as',
      title: 'See the app through their eyes',
      body: 'Once you\'ve added members at different levels, use the View As dropdown to experience the app exactly as each role will see it. If you change something while viewing as a member, it is saved under their name with a note that you entered it.', // P4-07 adds: "— and they get an email telling them."
      placement: 'bottom',
    },
  ],

  'import-your-org': [
    // Tim 26 Oct (D07.1 r1): start from the gear, then the Organization tab, so the path is never a mystery
    {
      route: '/',
      target: 'header-settings',
      title: 'Start at the gear',
      body: 'Settings live behind the gear in the top bar on every page — Your Settings, Organization, and DREW. Click it.',
      placement: 'bottom',
    },
    {
      route: '/settings/organization',
      target: 'settings-tab-organization',
      title: 'Organization',
      body: 'The Organization tab holds your structure, your members and roles, and the import. Everything about who is in REPSit.ai starts here.',
      placement: 'bottom',
    },
    {
      route: '/settings/organization',
      target: 'new-org',
      title: "Now it's time to get real",
      body: 'We want to make the setup process as quick and easy as possible by providing the ability to import your organization. Click "Import Organization" and drop the best representation of your current organizational structure. This can be a PDF org chart, a photo of the one on the wall, an HR spreadsheet, or plain text description. Your feedback as a beta tester on this step is of great importance.',
      placement: 'bottom',
    },
    {
      route: '/settings/organization',
      target: null,
      title: 'Edit and approve',
      body: 'REPSit.ai will propose your organizational structure: your sites, departments, and natural work teams (NWTs). It will show you the judgment calls it made. If changes are needed, you can make them on the spot. Nothing is created until you approve. Once you approve, REPSit.ai will be shaped like your operation.',
    },
  ],
};

export const hasTour = (key) => Boolean(TOURS[key]);
